import React from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getPoofAPIUrl } from '@/lib/config';
import { StatusChip } from './StatusChip';

interface Delivery {
  id: string;
  url: string;
  status: string;
  attemptCount: number;
  lastError?: string;
}

interface DeliveriesListProps {
  deliveries: Delivery[];
  apiKey: string;
}

export const DeliveriesList: React.FC<DeliveriesListProps> = ({ deliveries, apiKey }) => {
  const handleRefire = async (deliveryId: string) => {
    try {
      const url = getPoofAPIUrl(`/api/v1/webhooks/${deliveryId}/refire`);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
      });
      let json: any = null;
      try { json = await res.json(); } catch { /* not JSON */ }
      if (!res.ok || !json?.success) {
        toast.error(json?.error?.message || `Request failed (${res.status})`);
        return;
      }
      toast.success('Webhook queued for re-delivery');
    } catch {
      toast.error('Network error');
    }
  };

  if (!deliveries.length) {
    return (
      <div className='text-sm text-center py-6' style={{ color: 'var(--pp-text-subtle)' }}>
        No webhook deliveries
      </div>
    );
  }

  return (
    <div className='flex flex-col divide-y' style={{ borderColor: 'var(--pp-border)' }}>
      {deliveries.slice(0, 6).map((d) => (
        <div key={d.id} className='flex items-center justify-between py-2.5 gap-2'>
          <div className='flex flex-col min-w-0 flex-1'>
            <span className='text-xs truncate' style={{ color: 'var(--pp-text-muted)', maxWidth: 180 }}>
              {d.url}
            </span>
            <span className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
              Attempt {d.attemptCount}
              {d.lastError && (
                <span className='ml-1' style={{ color: 'var(--pp-danger)' }}>
                  — {d.lastError.slice(0, 40)}
                </span>
              )}
            </span>
          </div>
          <div className='flex items-center gap-2 flex-shrink-0'>
            <StatusChip status={d.status} />
            <button
              onClick={() => handleRefire(d.id)}
              className='p-1.5 rounded border hover:bg-gray-50 transition-colors'
              style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-muted)' }}
              title='Re-fire'
            >
              <RefreshCw size={13} strokeWidth={2} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DeliveriesList;
